import apiClient from './client'
import { SellerProgress } from './seller'

export interface SellerListingInfo {
  release_id: number
  listing_id: string
  price: number | null
  currency: string | null
  condition: string | null
  sleeve_condition: string | null
  seller_location: string | null
  uri: string | null
  comments: string | null
  first_seen: string | null
}

export interface EnrichmentProgress {
  total: number
  done: number
  running: boolean
  paused: boolean
  error?: string
  updatedAt: number
}

export interface SavedSeller {
  id: number
  username: string
  total_listings: number
  unique_releases: number
  status: 'pending' | 'ready' | 'error' | 'paused'
  error: string | null
  last_fetched_at: string | null
  created_at: string
  progress?: SellerProgress | null
  enrichment?: EnrichmentProgress | null
}

export interface SavedSellerDetail extends SavedSeller {
  release_ids: number[]
  enrichment?: EnrichmentProgress | null
}

export async function getSavedSellers(): Promise<SavedSeller[]> {
  const { data } = await apiClient.get('/sellers')
  return data.data
}

export async function getSavedSeller(username: string): Promise<SavedSellerDetail | null> {
  try {
    const { data } = await apiClient.get(`/sellers/${encodeURIComponent(username)}`)
    return data.data
  } catch (err: unknown) {
    if ((err as { response?: { status: number } }).response?.status === 404) return null
    throw err
  }
}

export async function addSavedSeller(username: string): Promise<void> {
  await apiClient.post('/sellers', { username })
}

export async function removeSavedSeller(username: string): Promise<void> {
  await apiClient.delete(`/sellers/${encodeURIComponent(username)}`)
}

export async function refreshSavedSeller(username: string): Promise<void> {
  await apiClient.post(`/sellers/${encodeURIComponent(username)}/refresh`)
}

export async function pauseSeller(username: string, target: 'fetch' | 'enrich'): Promise<void> {
  await apiClient.post(`/sellers/${encodeURIComponent(username)}/pause`, { target })
}

export async function resumeSeller(username: string, target: 'fetch' | 'enrich'): Promise<void> {
  await apiClient.post(`/sellers/${encodeURIComponent(username)}/resume`, { target })
}

export async function getSellerListings(username: string, releaseIds: number[]): Promise<SellerListingInfo[]> {
  const params = releaseIds.length ? { ids: releaseIds.join(',') } : {}
  const { data } = await apiClient.get(`/sellers/${encodeURIComponent(username)}/listings`, { params })
  return data.data
}
